import { Box, Typography } from "@mui/material";
import { useState } from "react";
import DefaultButton from "./buttons/DefaultButton";
import { colors } from "../../theme";

const DISMISSED_KEY = "shufl:cookieNoticeDismissed";

/**
 * Every cookie Shufl sets is strictly necessary - a signed-in session, CSRF
 * protection, and (temporarily) a Spotify access token for the "link a playlist"
 * flow. None of it is tracking or advertising, and EU law exempts strictly-necessary
 * cookies from requiring opt-in consent - only disclosure. So this is a notice with
 * one acknowledgement button, not a consent gate: there's nothing to opt out of, and
 * a fake "Reject" button here would imply otherwise.
 */
export default function CookieNotice() {
    const [dismissed, setDismissed] = useState(() => {
        try {
            return localStorage.getItem(DISMISSED_KEY) === "true";
        } catch {
            // Private-browsing Safari can throw on localStorage access - fail open
            // and just show the notice again rather than crashing the page.
            return false;
        }
    });

    if (dismissed) {
        return null;
    }

    const handleDismiss = () => {
        try {
            localStorage.setItem(DISMISSED_KEY, "true");
        } catch {
            // Nothing to persist to - it'll just show again next visit.
        }
        setDismissed(true);
    };

    return (
        <Box
            role="region"
            aria-label="Cookie notice"
            sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                flexWrap: "wrap",
                px: { xs: 2.5, sm: 4 },
                py: 2,
                backgroundColor: colors.elevated,
                borderTop: `1px solid ${colors.line}`,
                boxShadow: "0 -8px 24px rgba(0,0,0,0.35)",
            }}
        >
            <Typography sx={{ fontSize: 13, color: colors.textSecondary, maxWidth: 640 }}>
                Shufl uses cookies to keep you signed in, protect your account, and connect
                to Spotify when you link a playlist. That's all - no tracking, no advertising.
            </Typography>
            <DefaultButton text="Got it" size="small" onClick={handleDismiss} />
        </Box>
    );
}
